import { Store__System } from "@talentbait/shared-components";
import Reflux from "reflux";
import React from "react";

// using store system essentials
const mainStore = {
  ats: null,
  atsSelector: false,
  atsSource: "",
  companyLogoFromAPI: "",
  inviteData: null,
  organization: null,
  organizations: [],
  user: null,
  userLoading: false,
  userProfile: null
};

/**
 * HOC to inject Reflux stores into functional or class components.
 *
 * @param {React.ComponentType} WrappedComponent - The component to wrap.
 * @param {Array} stores - An array of Reflux stores to inject.
 * @param {Array} [storeKeys] - The keys to pull from the stores' state.
 * @returns {React.ComponentType} A new component with injected stores.
 */
function withRefluxStore(WrappedComponent, stores, storeKeys = []) {
  return class extends Reflux.Component {
    constructor(props) {
      super(props);
      this.state = {};
      this.stores = [Store__System, ...stores];
      if (storeKeys.length) {
        this.storeKeys = storeKeys;
      }
    }

    componentDidMount() {
      if (process.env.NODE_ENV !== "production") {
        this.validateStoreKeys();
      }
    }

    validateStoreKeys() {
      try {
        const storeStates = this.stores.reduce(
          (acc, store, index) => {
            if (store && typeof store === "object") {
              const storeName = (store.constructor && store.constructor.name) || `Store${index}`;
              acc[storeName] = Object.keys(store.state || {});
            }
            return acc;
          },
          { mainStore: Object.keys(mainStore) }
        );

        const allStoreKeys = Object.values(storeStates).flat();
        const invalidKeys = (this.storeKeys || []).filter((key) => !allStoreKeys.includes(key));

        // Check for invalid keys
        if (invalidKeys.length > 0) {
          const warnings = invalidKeys
            .map((key) => {
              const storesWithKey = Object.entries(storeStates)
                .filter(([, keys]) => keys?.includes(key))
                .map(([name]) => name);

              return storesWithKey.length === 0 ? `"${key}" is not found in any store` : null;
            })
            .filter(Boolean);

          if (warnings.length > 0) {
            const componentName =
              WrappedComponent.displayName || WrappedComponent.name || "UnnamedComponent";
            const warningMessage = `Invalid store keys detected in ${componentName}:\n${warnings.join(
              "\n"
            )}`;
            console.warn(warningMessage);
          }
        }

        // Check for duplicate keys
        const duplicateKeys = [];
        const keyOccurrences = {};
        (this.storeKeys || []).forEach((key) => {
          keyOccurrences[key] = (keyOccurrences[key] || 0) + 1;
          if (keyOccurrences[key] > 1 && !duplicateKeys.includes(key)) {
            duplicateKeys.push(key);
          }
        });

        if (duplicateKeys.length > 0) {
          const componentName =
            WrappedComponent.displayName || WrappedComponent.name || "UnnamedComponent";
          const duplicateMessage = `Duplicate store keys detected in ${componentName}:\n${duplicateKeys.join(
            "\n"
          )}`;
          console.warn(duplicateMessage);
        }
      } catch (error) {
        console.error("Error in validateStoreKeys:", error);
      }
    }

    render() {
      return <WrappedComponent {...this.state} {...this.props} />;
    }
  };
}

export default withRefluxStore;
